import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type {
  EinnahmeKommuneRow,
  EinnahmeKommuneWrite,
  EinnahmeLandRow,
  EinnahmeLandWrite,
  EinnahmeSonstigeRow,
  EinnahmeSonstigeWrite,
  FinanzierungAuswahl,
  FinanzierungAuswahlUpsertBody,
  LandAufschluesselung,
  UUID,
} from './types';

// --- Auswahl der Pauschalen (1:1 zum Plan) --------------------------------

export function useFinanzierung(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.finanzierung.plan(planId) : ['finanzierung', 'none'],
    queryFn: async (): Promise<FinanzierungAuswahl | null> => {
      if (!planId) return null;
      const res = await apiClient.get<FinanzierungAuswahl>(`/plan/${planId}/finanzierung`);
      return res.data;
    },
    enabled: !!planId,
  });
}

export function useUpsertFinanzierung(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: FinanzierungAuswahlUpsertBody): Promise<FinanzierungAuswahl> => {
      const res = await apiClient.put<FinanzierungAuswahl>(
        `/plan/${planId}/finanzierung`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.finanzierung.plan(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
      // Die Landesfoerderung-Aufschluesselung rechnet live aus Belegung,
      // Pauschalen-Auswahl und Kinderliste — sie muss hier mitziehen.
      void qc.invalidateQueries({
        queryKey: queryKeys.einnahmen.landAufschluesselung(planId),
      });
    },
  });
}

// --- Einnahmen Kommune ----------------------------------------------------

export function useEinnahmenKommune(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.einnahmen.kommune(planId) : ['einnahmen', 'kommune', 'none'],
    queryFn: async (): Promise<EinnahmeKommuneRow[]> => {
      if (!planId) return [];
      const res = await apiClient.get<EinnahmeKommuneRow[]>(
        `/plan/${planId}/einnahmen/kommune`,
      );
      return res.data;
    },
    enabled: !!planId,
  });
}

export function useCreateEinnahmeKommune(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EinnahmeKommuneWrite): Promise<EinnahmeKommuneRow> => {
      const res = await apiClient.post<EinnahmeKommuneRow>(
        `/plan/${planId}/einnahmen/kommune`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.einnahmen.kommune(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

export function useDeleteEinnahmeKommune(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<void> => {
      await apiClient.delete(`/plan/${planId}/einnahmen/kommune/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.einnahmen.kommune(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

// --- Einnahmen Land -------------------------------------------------------

export function useEinnahmenLand(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.einnahmen.land(planId) : ['einnahmen', 'land', 'none'],
    queryFn: async (): Promise<EinnahmeLandRow[]> => {
      if (!planId) return [];
      const res = await apiClient.get<EinnahmeLandRow[]>(`/plan/${planId}/einnahmen/land`);
      return res.data;
    },
    enabled: !!planId,
  });
}

export function useCreateEinnahmeLand(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EinnahmeLandWrite): Promise<EinnahmeLandRow> => {
      const res = await apiClient.post<EinnahmeLandRow>(
        `/plan/${planId}/einnahmen/land`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.einnahmen.land(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

export function useDeleteEinnahmeLand(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<void> => {
      await apiClient.delete(`/plan/${planId}/einnahmen/land/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.einnahmen.land(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

// --- Einnahmen Sonstige ---------------------------------------------------

export function useEinnahmenSonstige(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.einnahmen.sonstige(planId) : ['einnahmen', 'sonstige', 'none'],
    queryFn: async (): Promise<EinnahmeSonstigeRow[]> => {
      if (!planId) return [];
      const res = await apiClient.get<EinnahmeSonstigeRow[]>(
        `/plan/${planId}/einnahmen/sonstige`,
      );
      return res.data;
    },
    enabled: !!planId,
  });
}

export function useCreateEinnahmeSonstige(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EinnahmeSonstigeWrite): Promise<EinnahmeSonstigeRow> => {
      const res = await apiClient.post<EinnahmeSonstigeRow>(
        `/plan/${planId}/einnahmen/sonstige`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.einnahmen.sonstige(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

export function useDeleteEinnahmeSonstige(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<void> => {
      await apiClient.delete(`/plan/${planId}/einnahmen/sonstige/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.einnahmen.sonstige(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

/**
 * Aufschluesselung der Landesfoerderung — Satz, Anzahl und Betrag je Zeile.
 *
 * Der Endpunkt rechnet auf dem aktuellen Stand (nicht auf dem letzten
 * Snapshot) und nutzt dieselbe Engine-Funktion wie die Plansumme. Deshalb
 * wird die Abfrage bei jeder Aenderung an Belegung, Pauschalen-Auswahl oder
 * Integrationskindern ungueltig — sonst zeigte sie veraltete Zahlen.
 */
export function useLandAufschluesselung(planId: UUID | null) {
  return useQuery({
    queryKey: planId
      ? queryKeys.einnahmen.landAufschluesselung(planId)
      : ['einnahmen', 'land-aufschluesselung', 'none'],
    queryFn: async (): Promise<LandAufschluesselung> => {
      if (!planId) {
        return {
          stichtag_monat: 3,
          gesamt_kinder_stichtag: 0,
          bloecke: [],
          summe_jahr: '0',
        };
      }
      const res = await apiClient.get<LandAufschluesselung>(
        `/plan/${planId}/einnahmen/land-aufschluesselung`,
      );
      return res.data;
    },
    enabled: !!planId,
  });
}
